'use client';

import type { StoreHit } from '@/lib/algolia/stores';
import { getLanguageFromLocale } from '@/lib/i18n/config';
import { getTranslatedStoreValue } from '@/lib/stores/utils';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';

import styles from './BoutiqueDetailAbout.module.scss';

export interface BoutiqueDetailAboutProps {
  store: StoreHit;
}

/**
 * Two-column about section with image and boutique description.
 */
export function BoutiqueDetailAbout({ store }: Readonly<BoutiqueDetailAboutProps>) {
  const locale = useLocale();
  const localeSlug = getLanguageFromLocale(locale);
  const t = useTranslations('boutiquesDetail');

  const description = getTranslatedStoreValue(store, 'description', localeSlug) ?? '';
  const aboutImage =
    store.photo_gallery?.[1]?.image?.url ??
    store.photo_gallery?.[0]?.image?.url ??
    store.thumbnail_url;

  if (!description && !aboutImage) {
    return null;
  }

  const paragraphs = description
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <section className={styles.section} aria-labelledby="boutique-detail-about-heading">
      <div className={styles.inner}>
        {aboutImage ? (
          <div className={styles.media}>
            <Image
              src={aboutImage}
              alt=""
              fill
              className={styles.image}
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>
        ) : null}

        {paragraphs.length > 0 ? (
          <div className={styles.copy}>
            <h2 id="boutique-detail-about-heading" className={styles.visuallyHidden}>
              {t('aboutHeading')}
            </h2>
            {paragraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 40)} className={styles.paragraph}>
                {paragraph}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

BoutiqueDetailAbout.displayName = 'BoutiqueDetailAbout';
